import { useState } from 'react'
import { createLogger } from '@/shared/logging/logger'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import { resolveMediaUrl } from '@/features/media-library/utils/media-resolver'
import { updateMedia } from '@/infrastructure/storage'
import { setIdMapEntry } from './idmap'
import { updateItem } from '@/features/timeline/stores/actions/item-actions'
import { useTimelineStore } from '@/features/timeline/stores/timeline-store'
import type { VideoItem } from '@/types/timeline'
import { clipFileUrl, getCatalogClip } from './api'
import { CatalogPanel } from './catalog-panel'
import { makeFilename } from './filename'

const log = createLogger('smm-replace')

/**
 * Modal that reuses {@link CatalogPanel} in pick mode to swap the media backing
 * a single timeline VIDEO item. The item keeps its timeline position (`from`)
 * and length (`durationInFrames`); only the media reference and the
 * source-trim range are rewritten so the new clip plays from its own start —
 * matching the source-field shape a freshly added video item gets from
 * `media-timeline-item-builder` (`sourceStart: 0`, `sourceEnd: sourceDuration`,
 * `trimStart: 0`, `trimEnd: 0`, `sourceFps`, `sourceDuration`).
 */
export function ReplaceWithDialog({
  projectId,
  itemId,
  onClose,
}: {
  projectId: string
  itemId: string
  onClose: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function pick(catalogId: string) {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const meta = await mediaLibraryService.importMediaFromUrl(clipFileUrl(catalogId), projectId)
      setIdMapEntry(projectId, catalogId, meta.id) // round-trip safety for autosave

      // Enrich freecut's local MediaMetadata with catalog data so tags and
      // description appear in the media inspector. Fail-soft per spec §6.
      try {
        const clip = await getCatalogClip(catalogId)
        await updateMedia(meta.id, {
          tags: clip.keywords,
          description: clip.description ?? undefined,
          fileName: makeFilename({ id: clip.id, category: clip.category }),
        })
      } catch (e) {
        log.warn('enrichment skipped for', catalogId, e)
      }

      // Resolve the playable blob/proxy URL the same way the media-drop flow
      // does, so `src` points at the freshly imported media.
      const src = await resolveMediaUrl(meta.id)
      if (!src) {
        throw new Error(`could not resolve media url for ${meta.id}`)
      }
      const thumbnailUrl = await mediaLibraryService.getThumbnailBlobUrl(meta.id)

      const projectFps = useTimelineStore.getState().fps
      // Mirrors getMediaSourceTiming() for the 'video' branch (source-native FPS).
      const sourceFps = meta.fps || projectFps
      const sourceDuration = Math.max(1, Math.round(meta.duration * sourceFps))

      // Patch ONLY the media reference + source range; keep from/durationInFrames
      // (timeline position and length stay put). Reset trims so the new clip
      // plays from its start.
      const patch: Partial<VideoItem> = {
        mediaId: meta.id,
        src,
        thumbnailUrl: thumbnailUrl ?? undefined,
        sourceWidth: meta.width || undefined,
        sourceHeight: meta.height || undefined,
        sourceFps,
        sourceStart: 0,
        sourceEnd: sourceDuration,
        sourceDuration,
        trimStart: 0,
        trimEnd: 0,
        // Clear any prior offset/reverse-conform caches tied to the old media.
        offset: 0,
        isReversed: false,
        reverseConformSrc: undefined,
        reverseConformPath: undefined,
        reverseConformKey: undefined,
        reverseConformPreviewSrc: undefined,
        reverseConformPreviewPath: undefined,
        reverseConformPreviewKey: undefined,
        reverseConformStatus: undefined,
        reverseConformLocalStart: undefined,
      }

      updateItem(itemId, patch)
      onClose()
    } catch (e) {
      log.warn('replace failed', e)
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="flex h-[80vh] w-[420px] flex-col overflow-hidden rounded-lg border border-border bg-background"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2">
          <span className="text-sm font-medium">Replace clip{busy ? ' …' : ''}</span>
          <button type="button" onClick={onClose} className="text-muted-foreground">
            ✕
          </button>
        </div>
        {error && <div className="px-4 py-2 text-xs text-destructive">Replace failed: {error}</div>}
        <div className="min-h-0 flex-1">
          <CatalogPanel projectId={projectId} onPick={pick} />
        </div>
      </div>
    </div>
  )
}
